// src/components/Home.js
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { storage, auth, functions } from '../firebase/firebaseConfig';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { signInAnonymously } from 'firebase/auth';
import Modal from './Modal';
// CustomCamera removed; using native camera via file input capture
import { v4 as uuidv4 } from 'uuid';
import { isMobileDevice } from '../utils/deviceDetection';
import {
  generateSessionId, 
  logBehaviorEvent, 
  trackFileUpload, 
  updateFileStatus,
  trackProcessedImage,
  trackStatus 
} from '../services/firestoreTracking';
import Rectangle58 from '../images/Rectangle58.png';
import Rectangle59 from '../images/Rectangle59.png';
import Rectangle60 from '../images/Rectangle60.png';
import Group82 from '../images/Group82.png';
import Group83 from '../images/Group83.png';
import Group84 from '../images/Group84.png';
import './Home.css';

function Home({ capturedImage, onModalStateChange }) {
  const { t } = useTranslation();
  const [imageFile, setImageFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scannedImageURL, setScannedImageURL] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const fileInputRef = useRef();

  /**
   * Ensure anonymous auth is established before any action that needs it.
   * Called lazily (on file select / upload / camera capture) instead of on
   * every page load to avoid creating Firebase Auth accounts for casual visitors.
   */
  const ensureAnonymousAuth = async () => {
    if (auth.currentUser) return true;
    try {
      await signInAnonymously(auth);
      console.log('Sesión anónima iniciada (deferred)');
      return true;
    } catch (error) {
      console.error('Error al iniciar sesión anónima:', error);
      setErrorMessage('Error de autenticación. Por favor, recargue la página.');
      return false;
    }
  };
  
  // Process capturedImage when it changes (from global photo buttons)
  useEffect(() => {
    if (capturedImage) {
      // Reset all state variables when a new photo is captured
      // Pass current state values to properly clean up blob URLs
      clearAllStates(scannedImageURL, previewURL);

      // Generate new session ID for the new photo
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
      sessionStorage.setItem('sessionId', newSessionId);
      
      // Log session start
      if (auth.currentUser) {
        logBehaviorEvent('session_start', { sessionId: newSessionId });
      }

      // Set new image file and preview
      setImageFile(capturedImage);
      const preview = URL.createObjectURL(capturedImage);
      setPreviewURL(preview);
      
      // Open modal with new image — server validates face count
      setIsModalOpen(true);
      if (onModalStateChange) onModalStateChange(true);
    }
  }, [capturedImage]);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Ensure anonymous auth before any tracking/upload
    if (!(await ensureAnonymousAuth())) return;

    // Reset all state variables when a new photo is selected
    // Pass current state values to properly clean up blob URLs
    clearAllStates(scannedImageURL, previewURL);

    // Generate new session ID for the new photo
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    sessionStorage.setItem('sessionId', newSessionId);
    
    // Log session start
    if (auth.currentUser) {
      logBehaviorEvent('session_start', { sessionId: newSessionId });
    }

    // Set new image file and preview
    setImageFile(file);
    const preview = URL.createObjectURL(file);
    setPreviewURL(preview);
    
    // Open modal with new image — server validates face count
    setIsModalOpen(true);
    if (onModalStateChange) onModalStateChange(true);

    resetFileInput();
  };




  // Phone portraits are easily 5-12MB; the Firebase callable body cap is 10MB.
  // Downscale to a max edge of 1600px and re-encode as JPEG q=0.85 before
  // base64 — keeps the upload under the cap and matches the server's
  // 1024×1024 internal segmentation working size.
  const downscaleToBase64 = (file, maxEdge = 1600, quality = 0.85) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const longest = Math.max(w, h);
        const scale = longest > maxEdge ? maxEdge / longest : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('canvas toBlob returned null'));
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result || '';
              const idx = result.indexOf(',');
              resolve(idx >= 0 ? result.slice(idx + 1) : result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          },
          'image/jpeg',
          quality,
        );
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    });

  const handleUpload = async () => {
    if (!imageFile) return;

    if (!(await ensureAnonymousAuth())) return;

    setIsScanning(true);
    setUploadProgress(0);

    const userId = auth.currentUser.uid;
    const filename = `${userId}_${uuidv4()}`;
    setFileName(filename);
    const thisSessionId = sessionId;
    const filePath = `uploads/${userId}/${filename}.jpg`;

    // Fire-and-forget: keep an audit trail in Storage + Firestore so existing
    // analytics, admin tools, and email triggers still see the original upload.
    // The processed result no longer comes from Storage — it returns inline below.
    (async () => {
      try {
        const imageStorageRef = storageRef(storage, filePath);
        const uploadTask = uploadBytesResumable(imageStorageRef, imageFile);
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.warn('Upload audit copy failed (non-blocking):', error);
          },
          async () => {
            try {
              await trackFileUpload(filename, filePath, imageFile.size, thisSessionId);
              await trackStatus(userId, thisSessionId, 'uploaded', 'upload', 'Image uploaded successfully');
              await updateFileStatus(filename, 'processing');
            } catch (e) {
              console.warn('trackFileUpload failed (non-blocking):', e);
            }
          }
        );
      } catch (e) {
        console.warn('Audit upload setup failed (non-blocking):', e);
      }
    })();

    try {
      const imageB64 = await downscaleToBase64(imageFile);

      const processPhoto = httpsCallable(functions, 'processPhoto');
      const t0 = performance.now();
      const resp = await processPhoto({ image_b64: imageB64, filename });
      const elapsedMs = Math.round(performance.now() - t0);
      console.log(`processPhoto round-trip: ${elapsedMs}ms`);

      if (thisSessionId !== sessionId) {
        // User started a new photo while this one was in flight — discard
        return;
      }

      const data = resp && resp.data;
      if (!data || !data.success || !data.image_b64) {
        const reason = (data && (data.message || data.status)) || 'unknown error';
        console.error('processPhoto returned no image:', reason);
        setErrorMessage('No se pudo procesar la imagen: ' + reason);
        setIsScanning(false);
        return;
      }

      const dataUrl = `data:image/jpeg;base64,${data.image_b64}`;
      setScannedImageURL(dataUrl);
      setIsScanning(false);

      try {
        const processedPath = `downloads/${userId}/${filename}_portrait.jpg`;
        trackProcessedImage(filename, processedPath, thisSessionId);
        trackStatus(userId, thisSessionId, 'completed', 'processing', 'Image processed successfully');
      } catch (e) {
        console.warn('trackProcessedImage failed (non-blocking):', e);
      }
    } catch (error) {
      console.error('processPhoto callable failed:', error);
      setErrorMessage('Error al procesar la imagen. Por favor, inténtelo de nuevo.');
      setIsScanning(false);
    }
  };

  const clearAllStates = (currentScannedImageURL = scannedImageURL, currentPreviewURL = previewURL) => {
    // Revoke blob URLs to free memory (use current values passed as parameters)
    if (currentPreviewURL) {
      try {
        URL.revokeObjectURL(currentPreviewURL);
      } catch (error) {
        // Ignore errors when revoking URL
        console.warn('Error revoking preview URL:', error);
      }
    }
    
    // Revoke scanned image blob URL if it exists
    if (currentScannedImageURL && (currentScannedImageURL.startsWith('blob:') || currentScannedImageURL.startsWith('data:'))) {
      try {
        URL.revokeObjectURL(currentScannedImageURL);
      } catch (error) {
        // Ignore errors when revoking URL
        console.warn('Error revoking scanned image URL:', error);
      }
    }

    // Reset all state variables to initial values
    setImageFile(null);
    setPreviewURL(null);
    setUploadProgress(0);
    setErrorMessage('');
    setIsScanning(false);
    setScannedImageURL(null);
    // Note: sessionId will be set to a new UUID when a new photo is selected
  };

  const handleRetake = () => {
    // Pass current state values to properly clean up blob URLs
    clearAllStates(scannedImageURL, previewURL);
    setIsModalOpen(false);
    if (onModalStateChange) onModalStateChange(false);

    setTimeout(() => {
      if (isMobileDevice()) {
        setIsCameraOpen(true);
      } else {
        fileInputRef.current?.click();
      }
    }, 300);
  };
  
  const handleModalClose = () => {
    if (isScanning) {
      // Disallow closing during scanning
      alert('Por favor, espere hasta que finalice el procesamiento.');
      return;
    }

    clearAllStates();
    setIsModalOpen(false);
    if (onModalStateChange) onModalStateChange(false);
  };

  // Debug output
console.log("Renderizando componente Home, cámara abierta:", isCameraOpen);
  
  return (
    <div className="home-container">
      {/* Hero Section */}
      <div className="hero-section" style={styles.heroSection}>
        <h1 style={styles.title}>{t('title')}</h1>
        <p style={styles.subtitle}>{t('subtitle')}</p>
        
        {/* Guide Section */}
        <div className="guide-section">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <img 
                src={Rectangle58} 
                alt="" 
                style={{ width: '163px', height: '163px', objectFit: 'cover', borderRadius: '8px' }}
              />
              <img 
                src={Rectangle59} 
                alt="" 
                style={{ width: '163px', height: '163px', objectFit: 'cover', borderRadius: '8px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '7px', justifyContent: 'center', alignItems: 'center' }}>
              <img 
                src={Rectangle60} 
                alt="" 
                style={{ width: '163px', height: '163px', objectFit: 'cover', borderRadius: '8px' }}
              />
              <div style={{
                padding: '16px 12px',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '163px',
                height: '163px',
                boxSizing: 'border-box',
                backgroundColor: 'rgb(255, 210, 117)'
              }}>
                <div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: 'rgb(33, 33, 33)',
                    textAlign: 'center'
                  }}> Foto carnet fácil</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="pricing-section" style={styles.pricingSection}>
        <h2 style={styles.pricingTitle}>Que Ofrecemos?</h2>
        <div className="pricing-cards">
          <div className="pricing-card" style={{...styles.pricingCard, ...styles.digitalCard}}>
            <div>
              <div style={styles.cardTitle}>Foto Digital</div>
              <div style={styles.cardPrice}>€0.99</div>
            </div>
            <div style={styles.cardDescription}>
              1. Directamente en tu correo electrónico o tu movil<br/>
              2.listo para imprimir<br/>
              3. imagen ajustada a la normativa<br/>
            </div>
          </div>
          <div className="pricing-card" style={{...styles.pricingCard, ...styles.physicalCard}}>
            <div>
              <div style={styles.cardTitle}>Físico</div>
              <div style={styles.cardPrice}>€ 4.99</div>
            </div>
            <div style={styles.cardDescription}>
              1. Impresión de alta calidad<br/>
              2. Recorte profesional según los estándares de foto carnet<br/>
              3. Entrega directa a tu domicilio
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="how-it-works-section" style={styles.howItWorksSection}>
        <h2 style={styles.howItWorksTitle}>Como funciona?</h2>
        <div className="steps-container">
          <div style={styles.step}>
            <div style={styles.stepTitle}>1. Toma o sube la foto</div>
            <img 
              src={Group82} 
              alt="Tomar foto" 
              className="step-image"
              style={styles.stepImage}
              onError={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
                e.target.style.display = 'flex';
                e.target.style.alignItems = 'center';
                e.target.style.justifyContent = 'center';
                e.target.style.color = '#666';
                e.target.alt = 'Ilustración de toma de foto';
              }}
            />
          </div>
          <div style={styles.step}>
            <div style={styles.stepTitle}>2. Verifica tu foto</div>
            <img 
              src={Group83} 
              alt="Verificar foto" 
              className="step-image"
              style={styles.stepImage}
              onError={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
                e.target.style.display = 'flex';
                e.target.style.alignItems = 'center';
                e.target.style.justifyContent = 'center';
                e.target.style.color = '#666';
                e.target.alt = 'Ilustración de verificación';
              }}
            />
          </div>
          <div style={styles.step}>
            <div style={styles.stepTitle}>3. Paga y recibe tu foto</div>
            <img 
              src={Group84} 
              alt="Recibir fotos" 
              className="step-image"
              style={styles.stepImage}
              onError={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
                e.target.style.display = 'flex';
                e.target.style.alignItems = 'center';
                e.target.style.justifyContent = 'center';
                e.target.style.color = '#666';
                e.target.alt = 'Ilustración de recepción';
              }}
            />
          </div>
        </div>
      </div>

      {/* Photo Type Selection */}
      <div className="photo-type-section" style={styles.photoTypeSection}>
        <h2 style={styles.photoTypeTitle}>Para que necesitas?</h2>
        <div className="photo-type-cards">
          <div className="photo-type-card">
            <div style={styles.photoTypeIcon}>
              <img 
                src={process.env.PUBLIC_URL + '/images/emoji_passport.svg'} 
                alt="Pasaporte" 
                style={{ width: '22px', height: '36px' }}
              />
            </div>
            <div style={styles.photoTypeLabel}>Pasaporte</div>
          </div>
          <div className="photo-type-card">
            <div style={styles.photoTypeIcon}>
              <img 
                src={process.env.PUBLIC_URL + '/images/emoji_car.svg'} 
                alt="Coche" 
                style={{ width: '42px', height: '68px' }}
              />
            </div>
            <div style={styles.photoTypeLabel}>Carnet</div>
          </div>
          <div className="photo-type-card">
            <div style={styles.photoTypeIcon}>
              <img 
                src={process.env.PUBLIC_URL + '/images/emoji_id.svg'} 
                alt="Documento de identidad" 
                style={{ width: '28px', height: '45px' }}
              />
            </div>
            <div style={styles.photoTypeLabel}>DNI</div>
          </div>
        </div>
      </div>

      {/* SEO Content */}
      <div style={styles.seoContent}>
        <p>{t('seoDescription')}</p>
        <p>{t('seoTechnology')}</p>
      </div>

      {/* Legal Links Section */}
      <div style={styles.legalLinksSection}>
        <Link to="/legal" style={styles.legalLink}>
          {t('avisoLegal')}
        </Link>
        <Link to="/terms" style={styles.legalLink}>
          {t('terminosDevoluciones')}
        </Link>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Custom Camera Component */}
  {/* CustomCamera removed */}

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        imageSrc={previewURL}
        onUpload={handleUpload}
        onRetake={handleRetake}
        uploadProgress={uploadProgress}
        isScanning={isScanning}
        scannedImageURL={scannedImageURL}
        sessionId={sessionId}
        fileName={fileName}
      />
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '375px',
    margin: '0 auto',
    textAlign: 'center',
    fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '0',
    paddingBottom: '100px',
    backgroundColor: '#FBF5ED',
    minHeight: '100vh',
    position: 'relative',
    '@media (min-width: 768px)': {
      maxWidth: '600px',
    },
  },
  heroSection: {
    padding: '80px 15px 0',
    position: 'relative',
  },
  title: { 
    fontSize: '32px', 
    marginBottom: '8px',
    color: '#000',
    fontWeight: '700',
    lineHeight: '1.2',
    marginTop: '0',
  },
  subtitle: { 
    fontSize: '18px', 
    marginBottom: '30px',
    color: '#555',
    fontWeight: '400',
  },
  guideSection: {
    marginBottom: '30px',
    position: 'relative',
  },
  guideImage: {
    width: '100%',
    height: '200px',
    borderRadius: '8px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    objectFit: 'cover',
    position: 'relative',
  },
  guideOverlay: {
    position: 'absolute',
    top: '75px',
    left: '23px',
    color: 'white',
    fontSize: '32px',
    fontWeight: '700',
    fontFamily: 'SF Pro, sans-serif',
  },
  playButton: {
    position: 'absolute',
    bottom: '20px',
    left: '20px',
    padding: '8px 16px',
    backgroundColor: '#FFD275',
    borderRadius: '999px',
    border: 'none',
    color: 'rgba(0, 0, 0, 0.20)',
    fontSize: '20px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  pricingSection: {
    padding: '0 15px',
    marginBottom: '30px',
  },
  pricingTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#212121',
    marginBottom: '20px',
    textAlign: 'left',
  },
  pricingCards: {
    display: 'flex',
    gap: '20px',
    justifyContent: 'space-between',
  },
  pricingCard: {
    flex: '1',
    padding: '24px 17px',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    minHeight: '273px',
  },
  digitalCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
  },
  physicalCard: {
    backgroundColor: '#FFD275',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#212121',
    textAlign: 'center',
  },
  cardPrice: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#232221',
  },
  cardDescription: {
    fontSize: '12px',
    fontWeight: '510',
    color: '#232221',
    lineHeight: '16px',
    textAlign: 'center',
    width: '125px',
  },
  howItWorksSection: {
    padding: '0 15px',
    marginBottom: '30px',
  },
  howItWorksTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#000',
    marginBottom: '20px',
    textAlign: 'left',
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  stepTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#000',
    lineHeight: '20px',
  },
  stepImage: {
    width: '100%',
    height: '120px',
    borderRadius: '8px',
    objectFit: 'contain',
    objectPosition: 'center',
    backgroundColor: '#f8f8f8',
    padding: '8px',
    boxSizing: 'border-box',
  },
  photoTypeSection: {
    padding: '0 15px',
    marginBottom: '30px',
  },
  photoTypeTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#212121',
    marginBottom: '20px',
    textAlign: 'left',
  },
  photoTypeCards: {
    display: 'flex',
    gap: '9px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  photoTypeCard: {
    width: '108px',
    height: '120px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    position: 'relative',
    paddingTop: '14px',
    paddingBottom: '36px', // reserve space for the label to avoid collision
  },
  photoTypeIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  photoTypeLabel: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '14px',
    fontWeight: '700',
    color: '#212121',
    textAlign: 'center',
  },
  seoContent: {
    textAlign: 'left',
    margin: '20px 15px',
    padding: '15px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#444',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  legalLinksSection: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    padding: '20px 15px',
    marginBottom: '40px',
    flexWrap: 'wrap',
  },
  legalLink: {
    color: '#212121',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '8px 16px',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  features: {
    display: 'none', // Hide old features section
  },
  feature: {
    display: 'none', // Hide old feature cards
  },
};

export default Home;
